import { produce } from 'immer';
import { ExpeditionLocality, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { newExpeditionInitialData } from '@app/pages/expedition/create/data.ts';
import { DetailToFormContext, ExpeditionDetailForPrefill, mapExpeditionDetailToForm, toLocationNoteStops } from '@app/pages/expedition/create/mappers/detailToForm.ts';
import { MergedRoute, mergeRepeatedStops, sortStopsByDate } from '@app/pages/expedition/create/mergeRules.ts';
import { buildCarrierNoteWithLocationBlock } from '@utils/carrierNoteMerge.ts';
import { formatPriceRequest } from '@utils/priceUtils.ts';
import { isNotBlank, formatWeight } from '@utils/utils.tsx';
import { CargoTypeCodeEnum } from '@utils/constants.ts';
import { applyExpeditionDefaults, ExpeditionDefaultField } from '@app/pages/expedition/create/defaults.ts';
import { CargoType } from '@components/framework/input/InputCargoType/InputCargoType.tsx';
import { CarrierDistributionSelectorData, DistributionType } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { RebillingType } from '@components/framework/tags/InternalInfoRebillingTag.tsx';

/**
 * Folds several shipments into the one form that will replace them.
 *
 * Two rules run through all of it. A field the sources disagree on is left empty and reported,
 * rather than silently taking one shipment's answer — the user has to make that call. And anything
 * list-shaped is combined, because dropping a label or a document would lose data the sources had.
 */

export type MergeConflictField =
  | 'expeditionType'
  | 'truckType'
  | 'cargoType'
  | 'transportationType'
  | 'incoterms'
  | 'responsiblePerson'
  | 'disponentPerson'
  | 'pairingSymbol'
  | 'invoiceState'
  | 'rebilling'
  | 'internalNote'
  | 'distribution'
  | 'polandSent'
  | 'huEkaerNumber'
  | 'roUitNumber'
  | 'utnCode'
  | 'internalInfoCodes';

export interface MergeConflict {
  field: MergeConflictField;
  /** The distinct values the sources held, named the way the form names them. */
  values: string[];
}

/**
 * The conflicts that name a field carrying a default, under the name the form gives it.
 *
 * A field in here has been left empty because the shipments disagreed, so the defaults must not be
 * applied to it afterwards. Conflicts absent from this map name fields with no default at all.
 */
const CONFLICT_FIELD_TO_FORM_FIELD: Partial<Record<MergeConflictField, ExpeditionDefaultField>> = {
  cargoType: 'cargoType',
};

/**
 * What the merge needs from the app to describe a value in words.
 *
 * Injected rather than imported: the merge itself is pure data work, and keeping translations and
 * codebooks out of it is what lets the whole thing be tested without a React tree. The truck-type
 * codebook is already loaded by the page, so naming a truck type here costs no extra request.
 */
export interface MergeLabels {
  tr: (key: string, fallback: string) => string;
  truckTypeName: (code: string) => string | undefined;
}

/** Used when nothing better is available, e.g. in tests: every value describes itself. */
export const rawLabels: MergeLabels = { tr: (_key, fallback) => fallback, truckTypeName: () => undefined };

/** What the merge did, for saying so plainly before and after it happens. */
export interface MergeSummary {
  shipmentCount: number;
  /** Stops that ended up on the route, and how many were folded away to get there. */
  stopCount: number;
  foldedStopCount: number;
  orderCount: number;
  /** Things that were combined rather than chosen between, worth naming so nothing looks lost. */
  combined: { field: 'labels' | 'attachments' | 'notifiedTargets' | 'specialRequirements' | 'customsDeclarationCompanies' | 'folders'; count: number }[];
  /** Set when the shipments came with a carrier: what happened to it and to their prices. */
  distribution?: MergeDistributionInfo;
}

export interface MergeResult {
  values: NewExpeditionForm;
  conflicts: MergeConflict[];
  /** Same places that could not be folded into one stop, with the reason. */
  keptApart: MergedRoute['keptApart'];
  /** Contact details that differed between stops folded into one, and were left empty. */
  contactConflicts: MergedRoute['contactConflicts'];
  /** What the merge managed to do, as opposed to what it could not decide. */
  summary: MergeSummary;
}

/** A stop that picks something up starts the route; everything else is a drop-off. */
const isLoadingStop = (loc: ExpeditionLocality): boolean => (loc.orders?.length ?? 0) > 0;

/**
 * A value in words, for anything that has not been given a label of its own.
 *
 * Names first, then the codes a person might still recognise. An id is the last resort — it says
 * nothing to the user, so a field that can only be described that way is a field worth labelling
 * properly below.
 */
const describe = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  const named = value as { name?: string; code?: string; incotermsValue?: string; cargoTypeCode?: string; sentNumber?: string; id?: string };
  return named.name ?? named.code ?? named.incotermsValue ?? named.cargoTypeCode ?? named.sentNumber ?? named.id ?? '—';
};

/**
 * Takes the shared value, or records a conflict and leaves the field alone.
 *
 * Sources that simply have nothing to say are ignored: one shipment carrying an Incoterm and
 * another carrying none is not a disagreement.
 *
 * Deciding whether two values are the same and describing them to the user are two different jobs,
 * so they are two different functions. `key` may be as technical as it likes — it is never shown —
 * while `label` is what the user reads and must never fall back to an id when a name exists.
 */
const resolveScalar = <T>(
  field: MergeConflictField,
  values: T[],
  conflicts: MergeConflict[],
  { key = describe, label = describe }: { key?: (value: T) => string; label?: (value: T) => string } = {},
): T | undefined => {
  const stated = values.filter(value => value !== null && value !== undefined && value !== '');
  if (stated.length === 0) return undefined;

  const distinct = new Map(stated.map(value => [key(value), value]));
  if (distinct.size === 1) return stated[0];

  conflicts.push({ field, values: Array.from(distinct.values()).map(label) });
  return undefined;
};

/** Joins the parts of a compound value the way the form shows them, dropping the parts that are empty. */
const compose = (...parts: (string | undefined)[]): string => parts.filter(part => (part || '').trim().length > 0).join(' · ') || '—';

/**
 * Names a carrier selection: who was picked, on what terms, for how much.
 *
 * This is the value that used to be shown as the JSON identity key, ids and all.
 */
export const describeDistribution = (value: CarrierDistributionSelectorData, labels: MergeLabels): string => {
  const carriers = (value?.selectedItems || []).map(item => item.name || item.id);
  const price = value?.price?.price != null ? formatPriceRequest(value.price) : undefined;

  // Nothing to look up when the field was never filled in — asking anyway would render whatever the
  // translation of "DistributionType.undefined" happens to be.
  const type = value?.distributionType ? labels.tr(`DistributionType.${value.distributionType}`, value.distributionType) : undefined;

  return compose(type, carriers.length > 0 ? carriers.join(', ') : undefined, price);
};

/** Names a cargo type the way `CargoTypeTag` does, ADR weight included. */
export const describeCargoType = (value: CargoType, labels: MergeLabels): string => {
  if (!value?.cargoTypeCode) return '—';

  const name = labels.tr(`CargoType.${value.cargoTypeCode}`, value.cargoTypeCode);
  return value.cargoTypeCode === CargoTypeCodeEnum.ADR_LQ ? compose(name, formatWeight(value.adrWeight)) : name;
};

/** Names a rebilling the way `InternalInfoRebillingTag` does: the kind, then who it goes to. */
export const describeRebilling = (value: RebillingType, labels: MergeLabels): string =>
  compose(
    value?.invoiceRebillingType ? labels.tr(`InternalInfoRebillingTag.${value.invoiceRebillingType}`, value.invoiceRebillingType) : undefined,
    value?.invoicePartner?.companyName,
    value?.invoiceDepartment,
  );

/** What the carrier selection of the merged shipment came out as, for saying so in the panel and the dialog. */
export interface MergeDistributionInfo {
  /** True when the sources brought a carrier with them and the merge kept it as a direct assignment. */
  assignedDirectly: boolean;
  /** Every carrier the sources had picked, named — shown whether or not one could be carried over. */
  carriers: string[];
  /** The carrier the merged shipment starts with, when the sources all named the same one. */
  selectedCarrier?: string;
  /** What each source was to be paid, as it stood. */
  prices: string[];
  /** The sum, when every source priced in the same currency. */
  totalPrice?: string;
}

/**
 * The carrier selection for a merge of shipments that already have one.
 *
 * A merged shipment replaces work already placed with a carrier, so it starts as a direct
 * assignment rather than sending the same job out to tender again. Where the sources agree — one
 * carrier, one currency — the merge carries the answer over, the price being the sum of what the
 * shipments were each going to cost. Where they disagree it carries nothing and reports what the
 * sources held: which carrier or which currency should win is not the merge's decision to make.
 */
export const mergeDistribution = (distributions: CarrierDistributionSelectorData[]): { value: CarrierDistributionSelectorData; info: MergeDistributionInfo } => {
  const stated = distributions.filter(Boolean);
  const template = stated.find(distribution => distribution.distributionType === DistributionType.ASSIGN) ?? stated[0];

  const carriersById = new Map(
    stated
      .flatMap(distribution => distribution.selectedItems || [])
      .filter(carrier => carrier?.id)
      .map(carrier => [carrier.id, carrier]),
  );
  const onlyCarrier = carriersById.size === 1 ? Array.from(carriersById.values())[0] : undefined;

  const priced = stated.filter(distribution => distribution.price?.price);
  const currencies = new Set(priced.map(distribution => distribution.price.currency));
  // One currency and the merge can add up; several and only the user knows what the total should be.
  const total =
    currencies.size === 1 ? { price: priced.reduce((sum, distribution) => sum + distribution.price.price, 0), currency: priced[0].price.currency } : undefined;

  return {
    value: {
      ...template,
      distributionType: DistributionType.ASSIGN,
      selectedItems: onlyCarrier ? [onlyCarrier] : [],
      price: total,
    },
    info: {
      assignedDirectly: true,
      carriers: Array.from(carriersById.values()).map(carrier => carrier.name || carrier.id),
      selectedCarrier: onlyCarrier ? onlyCarrier.name || onlyCarrier.id : undefined,
      prices: priced.map(distribution => formatPriceRequest(distribution.price)),
      totalPrice: total ? formatPriceRequest(total) : undefined,
    },
  };
};

const unionBy = <T>(lists: T[][], key: (item: T) => string): T[] => {
  const byKey = new Map<string, T>();
  lists.flat().forEach(item => {
    if (item !== null && item !== undefined) byKey.set(key(item), item);
  });
  return Array.from(byKey.values());
};

export const mergeExpeditionsToForm = (details: ExpeditionDetailForPrefill[], ctx: DetailToFormContext, labels: MergeLabels = rawLabels): MergeResult => {
  // The form keeps code-list ids only, but the shipments they came from carry the names as well —
  // so a disagreement can be reported as "Cost centre A / Cost centre B" rather than as two uuids.
  const codeListNames = new Map<string, string>();
  details.forEach(detail =>
    [
      detail.internalInfo?.internalInfoAccount,
      detail.internalInfo?.internalInfoCostCenter,
      detail.internalInfo?.internalInfoCostUnit,
      detail.internalInfo?.internalInfoProject,
    ].forEach(codeList => {
      if (codeList?.id) codeListNames.set(codeList.id, codeList.name || codeList.externalId || codeList.id);
    }),
  );
  const codeListLabel = (id: string) => codeListNames.get(id) || id;

  // Each source goes through the ordinary prefill first, so a merge sees exactly the same shape a
  // copy would — including freshly generated order ids, which is what keeps them unique here.
  // Block body on purpose: `mapExpeditionDetailToForm` returns the distribution it pre-filled, and
  // a value returned from an immer recipe replaces the state instead of amending it.
  const sources = details.map(detail =>
    produce(newExpeditionInitialData(), draft => {
      mapExpeditionDetailToForm(detail, draft, ctx);
    }),
  );

  const conflicts: MergeConflict[] = [];
  // Sources that already have a carrier on them; what they carry decides how the merged shipment
  // starts, and what the panel and the confirm dialog have to say about it.
  const assigned = sources.filter(source => source.distribution?.selectedItems?.length > 0 || source.distribution?.distributionType === DistributionType.ASSIGN);
  let distributionInfo: MergeDistributionInfo | undefined;

  const merged = produce(newExpeditionInitialData(), draft => {
    // Pick-ups first, then the drop-offs, each group in the order it is wanted rather than in the
    // order the shipments happened to be selected. Sorting inside the groups cannot put an order's
    // unloading before its loading, because every pick-up still precedes every drop-off.
    const loadings = sortStopsByDate(sources.flatMap(source => source.locations.filter(isLoadingStop)));
    const unloadings = sortStopsByDate(sources.flatMap(source => source.locations.filter(loc => !isLoadingStop(loc))));
    draft.locations = [...loadings, ...unloadings];

    // Enum-valued fields are named through the same keys their own tags use, so a conflict reads
    // the way the field reads everywhere else in the form.
    draft.expeditionType = resolveScalar(
      'expeditionType',
      sources.map(s => s.expeditionType),
      conflicts,
      {
        label: value => labels.tr(`ExpeditionTypeTag.${value}`, String(value)),
      },
    );
    draft.truckTypeCode = resolveScalar(
      'truckType',
      sources.map(s => s.truckTypeCode),
      conflicts,
      {
        label: code => labels.truckTypeName(code) || code,
      },
    );
    draft.transportationType = resolveScalar(
      'transportationType',
      sources.map(s => s.transportationType),
      conflicts,
      {
        label: value => labels.tr(`TransportationTypeEnum.${value}`, String(value)),
      },
    );
    draft.incoterms = resolveScalar(
      'incoterms',
      sources.map(s => s.incoterms),
      conflicts,
    );
    draft.person = resolveScalar(
      'responsiblePerson',
      sources.map(s => s.person),
      conflicts,
    );
    draft.disponentPerson = resolveScalar(
      'disponentPerson',
      sources.map(s => s.disponentPerson),
      conflicts,
    );
    draft.pairingSymbol = resolveScalar(
      'pairingSymbol',
      sources.map(s => s.pairingSymbol),
      conflicts,
    );
    draft.invoiceState = resolveScalar(
      'invoiceState',
      sources.map(s => s.invoiceState),
      conflicts,
      {
        label: value => labels.tr(`InvoiceStateEnum.${value}`, String(value)),
      },
    );
    draft.rebilling = resolveScalar(
      'rebilling',
      sources.map(s => s.rebilling),
      conflicts,
      {
        key: value => `${value?.invoiceRebillingType}|${value?.invoicePartner?.companyId ?? ''}|${value?.invoiceDepartment ?? ''}`,
        label: value => describeRebilling(value, labels),
      },
    );
    draft.internalNote = resolveScalar(
      'internalNote',
      sources.map(s => s.internalNote),
      conflicts,
    );
    draft.polandSent = resolveScalar(
      'polandSent',
      sources.map(s => s.polandSent),
      conflicts,
    );
    draft.huEkaerNumber = resolveScalar(
      'huEkaerNumber',
      sources.map(s => s.huEkaerNumber),
      conflicts,
    );
    draft.roUitNumber = resolveScalar(
      'roUitNumber',
      sources.map(s => s.roUitNumber),
      conflicts,
    );
    draft.utnCode = resolveScalar(
      'utnCode',
      sources.map(s => s.utnCode),
      conflicts,
    );

    // The cargo type carries the ADR weight with it, so the pair is resolved together. Assigned
    // unconditionally like every other field: a disagreement has to leave the tag empty, because a
    // cargo type none of the shipments had is worse than no answer at all.
    draft.cargoType = resolveScalar(
      'cargoType',
      sources.map(s => s.cargoType),
      conflicts,
      {
        key: value => `${value?.cargoTypeCode}|${value?.adrWeight ?? ''}`,
        label: value => describeCargoType(value, labels),
      },
    );

    // A shipment that already has a carrier is merged differently: the work is placed, so the
    // merged shipment keeps it as a direct assignment instead of going back out to tender. For
    // shipments that never reached a carrier the old rule stands — an all-or-nothing selection,
    // since half a carrier selection is worse than none.
    if (assigned.length > 0) {
      const merged = mergeDistribution(sources.map(s => s.distribution));
      draft.distribution = merged.value;
      distributionInfo = merged.info;

      // Not a silent loss: the user is told which carriers were in play and asked to pick one.
      if (merged.info.carriers.length > 1) conflicts.push({ field: 'distribution', values: merged.info.carriers });
    } else {
      // Compared by ids so two identical selections are recognised as one, reported by carrier name.
      // Falls back to null rather than undefined: the form's own blank shape uses null, and the
      // distribution schema cannot validate an undefined object — it throws instead of reporting.
      draft.distribution =
        resolveScalar(
          'distribution',
          sources.map(s => s.distribution),
          conflicts,
          {
            key: value =>
              JSON.stringify({
                type: value?.distributionType,
                price: value?.price,
                carriers: (value?.selectedItems || []).map(item => item.id).sort(),
              }),
            label: value => describeDistribution(value, labels),
          },
        ) ?? null;
    }

    draft.labels = unionBy(
      sources.map(s => s.labels || []),
      label => label.labelId,
    );
    draft.specialRequirements = Array.from(new Set(sources.flatMap(s => s.specialRequirements || [])));
    draft.loadingTypes = Array.from(new Set(sources.flatMap(s => s.loadingTypes || [])));
    draft.customsDeclarationCompanies = unionBy(
      sources.map(s => s.customsDeclarationCompanies || []),
      company => company.id,
    );
    // RD-2977: folders are a set, so merging combines them instead of flagging a conflict
    draft.folders = unionBy(
      sources.map(s => s.folders || []),
      folder => folder.id,
    );
    draft.notifiedTargets = unionBy(
      sources.map(s => s.notifiedTargets || []),
      target => `${target.notifyTargetType}|${target.id}`,
    );
    draft.attachments = unionBy(
      sources.map(s => s.attachments || []),
      attachment => attachment.attachmentId,
    );

    // The four code-list fields share one conflict entry: they are internal bookkeeping, and naming
    // each one separately would bury the differences that matter.
    const codeListConflicts: MergeConflict[] = [];
    const codeList = { label: codeListLabel };
    draft.internalInfoAccountCodeListId = resolveScalar(
      'internalInfoCodes',
      sources.map(s => s.internalInfoAccountCodeListId),
      codeListConflicts,
      codeList,
    );
    draft.internalInfoCostCenterCodeListId = resolveScalar(
      'internalInfoCodes',
      sources.map(s => s.internalInfoCostCenterCodeListId),
      codeListConflicts,
      codeList,
    );
    draft.internalInfoCostUnitCodeListId = resolveScalar(
      'internalInfoCodes',
      sources.map(s => s.internalInfoCostUnitCodeListId),
      codeListConflicts,
      codeList,
    );
    draft.internalInfoProjectCodeListId = resolveScalar(
      'internalInfoCodes',
      sources.map(s => s.internalInfoProjectCodeListId),
      codeListConflicts,
      codeList,
    );
    if (codeListConflicts.length > 0) conflicts.push({ field: 'internalInfoCodes', values: codeListConflicts.flatMap(c => c.values) });
  });

  // Folding repeated stops needs the assignments in place, so it runs on the composed route.
  const route = mergeRepeatedStops(merged, merged.locations);

  // A parameter none of the shipments answered gets the ordinary default. One they answered
  // differently does not: that field was left empty on purpose and is the user's to decide.
  const values: NewExpeditionForm = applyExpeditionDefaults(
    {
      ...merged,
      locations: route.locations,
      carrierNote: buildCarrierNoteWithLocationBlock(
        sources
          .map(source => source.carrierNote)
          .filter(isNotBlank)
          .join('\n'),
        toLocationNoteStops(route.locations),
      ),
    },
    conflicts.map(conflict => CONFLICT_FIELD_TO_FORM_FIELD[conflict.field]).filter(Boolean),
  );

  // Counted from the composed route rather than from the sources, so it says what actually happened.
  const combined: MergeSummary['combined'] = [
    { field: 'labels' as const, count: values.labels?.length ?? 0 },
    { field: 'attachments' as const, count: values.attachments?.length ?? 0 },
    { field: 'notifiedTargets' as const, count: values.notifiedTargets?.length ?? 0 },
    { field: 'specialRequirements' as const, count: values.specialRequirements?.length ?? 0 },
    { field: 'customsDeclarationCompanies' as const, count: values.customsDeclarationCompanies?.length ?? 0 },
    { field: 'folders' as const, count: values.folders?.length ?? 0 },
  ].filter(entry => entry.count > 0);

  const summary: MergeSummary = {
    shipmentCount: details.length,
    stopCount: route.locations.length,
    foldedStopCount: merged.locations.length - route.locations.length,
    orderCount: route.locations.reduce((count, loc) => count + (loc.orders?.length ?? 0), 0),
    combined,
    distribution: distributionInfo,
  };

  return { values, conflicts, keptApart: route.keptApart, contactConflicts: route.contactConflicts, summary };
};
